import { apiUpload, ApiError } from "./api.js";
import { pickRecorderMime } from "./useVoiceRecorder.js";

/**
 * The RECORDED fallback engine behind both dictation hooks
 * (`lib/useSpeechToText` — staff chat composer — and
 * `otto-agent/useSpeechToText`), ported from the embedded Otto widget's
 * dual-engine `setupDictation` (xop-edge-customer chat.js, 2026-07-16).
 *
 * Several real environments have a WORKING microphone and a platform
 * recognizer that silently yields nothing: the desktop app's macOS WKWebView
 * (wry implements WebKit's media-capture permission delegate but NOT its
 * separate speech-recognition one, so the recognizer never delivers),
 * WebView2 / non-branded Chromium builds (no Google speech-service keys), and
 * Firefox (no SpeechRecognition at all). While the live engine listens, the
 * hooks therefore ALSO record the mic via `startDictationRecorder()`; when a
 * session ends with ZERO recognized text, the clip goes to
 * `transcribeDictationClip()` → `POST /api/otto/transcribe` → the gateway's
 * Whisper.
 *
 * THE PRIVACY PROPERTY (do not weaken): audio only leaves the machine when
 * the platform recognizer produced nothing. A session that yielded live text
 * must `discard()` its recording — never post it.
 *
 * `benchRecognizer()` is the widget's sticky `serverMode`: the first time the
 * live engine proves dead (a recognition error while the mic itself works),
 * it is benched for the rest of the page and later sessions go straight to
 * record-and-transcribe.
 */

/** Below this many bytes there is no speech in the clip — just container
 * header (the widget uses the same floor). Skipping the upload also keeps
 * Whisper from hallucinating pleasantries on silence. */
const SILENCE_FLOOR_BYTES = 1500;

/** Timeslice for MediaRecorder — a crash loses ≤1s of audio, not all of it. */
const CHUNK_MS = 1000;

let recognizerBenched = false;

/** Whether the live recognizer has been benched (proved dead) this page. */
export function isRecognizerBenched(): boolean {
  return recognizerBenched;
}

/** Bench the live recognizer for the rest of the page — later dictation
 * sessions skip it and run record-and-transcribe only. */
export function benchRecognizer(): void {
  recognizerBenched = true;
}

export function _resetDictationFallbackForTests(): void {
  recognizerBenched = false;
}

/** Whether the recorded engine can run here (getUserMedia + MediaRecorder). */
export function canRecordDictation(): boolean {
  return (
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === "function" &&
    typeof MediaRecorder !== "undefined"
  );
}

export interface DictationRecorder {
  /** Stop capturing, release the mic, and resolve the assembled clip — or
   * null when nothing usable was captured (mic blocked, tiny/silent clip,
   * already discarded). Idempotent: later calls resolve null. */
  finish(): Promise<Blob | null>;
  /** Stop capturing and release the mic, dropping the clip (the live engine
   * won, or a send cleared the composer). */
  discard(): void;
  /** Whether getUserMedia rejected this session (mic blocked / absent). */
  micFailed(): boolean;
}

/**
 * Start recording the mic for the current dictation session. Returns null
 * where recording is unsupported (that environment simply has no rescue
 * path). The recorder must ALWAYS be settled via `finish()` or `discard()`
 * — both release the mic; only `finish()` may yield bytes.
 */
export function startDictationRecorder(): DictationRecorder | null {
  if (!canRecordDictation()) return null;

  let stream: MediaStream | null = null;
  let recorder: MediaRecorder | null = null;
  let chunks: Blob[] = [];
  let baseType = "audio/webm";
  let ended = false;
  let failed = false;

  const ready = navigator.mediaDevices
    .getUserMedia({ audio: true })
    .then((s) => {
      if (ended) {
        // Stopped / discarded while the permission prompt was up — release
        // the late stream immediately, never arm the recorder.
        s.getTracks().forEach((t) => t.stop());
        return;
      }
      stream = s;
      const picked = pickRecorderMime();
      baseType = picked?.baseMime ?? "audio/webm";
      try {
        recorder = picked?.recorderMime
          ? new MediaRecorder(s, { mimeType: picked.recorderMime })
          : new MediaRecorder(s);
      } catch {
        // The chosen mimeType wasn't actually accepted — fall back to default.
        try {
          recorder = new MediaRecorder(s);
        } catch {
          recorder = null;
          return;
        }
      }
      recorder.ondataavailable = (e: BlobEvent) => {
        if (e.data && e.data.size > 0) chunks.push(e.data);
      };
      try {
        recorder.start(CHUNK_MS);
      } catch {
        recorder = null;
      }
    })
    .catch(() => {
      failed = true;
    });

  function release(): void {
    stream?.getTracks().forEach((t) => {
      try {
        t.stop();
      } catch {
        // Already stopped.
      }
    });
    stream = null;
  }

  /** Stop the recorder (flushing its final chunk), release the mic, and
   * assemble the clip. The mic release ALWAYS happens. */
  async function harvest(): Promise<Blob | null> {
    await ready;
    const r = recorder;
    recorder = null;
    if (r && r.state !== "inactive") {
      await new Promise<void>((resolve) => {
        let settled = false;
        const settle = (): void => {
          if (!settled) {
            settled = true;
            resolve();
          }
        };
        r.onstop = settle;
        try {
          r.stop();
        } catch {
          settle();
        }
        // Belt & braces: a WebKit that never fires onstop must still release
        // the mic and settle the session.
        setTimeout(settle, 1500);
      });
    }
    release();
    if (chunks.length === 0) return null;
    const blob = new Blob(chunks, { type: baseType });
    chunks = [];
    return blob.size > SILENCE_FLOOR_BYTES ? blob : null;
  }

  return {
    micFailed: () => failed,
    discard(): void {
      if (ended) return;
      ended = true;
      void harvest().then(() => undefined);
    },
    async finish(): Promise<Blob | null> {
      if (ended) return null;
      ended = true;
      return await harvest();
    },
  };
}

/** Why a transcription attempt failed — the hooks map these onto their
 * `onError` codes so composers can toast something useful. */
export class DictationTranscribeError extends Error {
  readonly code: "transcribe-unavailable" | "transcribe-failed";
  constructor(code: "transcribe-unavailable" | "transcribe-failed", cause?: unknown) {
    super(code);
    this.name = "DictationTranscribeError";
    this.code = code;
    if (cause !== undefined) (this as { cause?: unknown }).cause = cause;
  }
}

/** The extension carries the container hint the gateway's decoder sniffs. */
const EXT_BY_MIME: Record<string, string> = {
  "audio/webm": "webm",
  "audio/mp4": "mp4",
  "audio/ogg": "ogg",
};

/**
 * POST one recorded dictation clip to the dashboard server's STT proxy
 * (`/api/otto/transcribe` → gateway `/agent/transcribe`, Whisper) and return
 * the transcript ("" when the clip held no recognizable speech). Only ever
 * call this for a session whose live engine produced NOTHING — see the
 * privacy property above.
 */
export async function transcribeDictationClip(blob: Blob): Promise<string> {
  const base = (blob.type.split(";")[0] ?? "").trim().toLowerCase() || "audio/webm";
  const ext = EXT_BY_MIME[base] ?? "webm";
  const file = new File([blob], `dictation.${ext}`, { type: base });
  try {
    const res = await apiUpload<{ text?: string | null }>("/api/otto/transcribe", file);
    return (res.text ?? "").trim();
  } catch (err) {
    const status = err instanceof ApiError ? err.status : 0;
    throw new DictationTranscribeError(
      status === 503 ? "transcribe-unavailable" : "transcribe-failed",
      err,
    );
  }
}
